import React from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import Select, { SingleValue } from 'react-select'
import { useAtomValue, useSetAtom } from 'jotai'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as lib from '../../lib'
import * as state from '../../states'
import {
	ERoleAccess,
	type IAccountBuyer,
	type IRevenueAgencyDropDown,
	type IRevenueSpend
} from '../../types'
import {
	revenueAgencyOptionValue,
	revenueAgencyOptionLabel,
	revenueAgencyFormatOptionLabel
} from './helpers'
import {
	accountOptionValue,
	accountOptionLabel,
	accountFormatOptionLabel
} from '../offerSpend/helpers'
import * as helpers from '../../helpers'
import { reactSelectStyles } from '../filters'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IRevenueSpendEditModalProps = {
	item: IRevenueSpend
	show: boolean
	accounts: Array<IAccountBuyer> | null
	revenueAgencies: Array<IRevenueAgencyDropDown> | null
	onClose: () => void
	onSave: () => void
}

export const RevenueSpendEditModal = (
	props: IRevenueSpendEditModalProps
) => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const {
		item,
		accounts: allAccounts,
		revenueAgencies: allRevenueAgencies,
		show,
		onClose,
		onSave
	} = props

	const today = React.useMemo(() => dt.utc(new Date()).startOf('day'), [])
	const id = item?.id ?? 0
	const [revenueAgencyId, setRevenueAgencyId] = React.useState<number>(item.agency?.id ?? 0)
	const [accountId, setAccountId] = React.useState<number>(item.account?.id ?? 0)
	const [at, setAt] = React.useState<Date>(dt.utc(item.at).toDate())
	const [amountInput, setAmountInput] = React.useState<string>(helpers.formatAmount(item.amount))
	const [notes, setNotes] = React.useState<string>(item.notes ?? '')


	if (!show) {
		return null
	}

	const onUpdate = async () => {
		const uri: string = `revenue/spend/${id}`
		const method: string = 'PATCH'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(tokens?.access)
		try {
			const body: string = JSON.stringify({
				agencyId: revenueAgencyId,
				accountId,
				at: dt.utc(at)?.format('YYYY-MM-DD') ?? null,
				amount: !!amountInput ? new Decimal(amountInput).toNumber() : 0,
				notes
			})
			const options: lib.FetchOptions = { body, method, params, headers }
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const currentAccount = allAccounts?.find(_ => _.id === accountId) ?? null
	const currentAccountLoginKt = currentAccount?.extra?.keitaroLogin ?? ''
	const modalTitle = `${t(ALL_LANG_KEY.revenue_spend_crud_modal.title_edit)} #${item?.id}`

	return <>
		<Modal show={show} onHide={onClose} className={'scroll'}>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form>
					<Form.Group>

						<Form.Label className="text-dark" htmlFor="select-revenue-agency">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_agency_label)}`}</Form.Label>
						<Select
							inputId="select-revenue-agency"
							className="react-select-container"
							options={allRevenueAgencies ?? []}
							value={allRevenueAgencies?.find(_ => _.id === revenueAgencyId) ?? null}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							getOptionValue={revenueAgencyOptionValue}
							getOptionLabel={revenueAgencyOptionLabel}
							formatOptionLabel={revenueAgencyFormatOptionLabel}
							styles={reactSelectStyles<IRevenueAgencyDropDown, false>(isThemeDark)}
							onChange={(_: SingleValue<IRevenueAgencyDropDown>) => setRevenueAgencyId(_?.id ?? 0)}
							isDisabled={false}
							isSearchable={true}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="select-account">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_account_label)}`}</Form.Label>
						<Select
							inputId="select-account"
							className="react-select-container"
							options={allAccounts ?? []}
							value={currentAccount}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							getOptionValue={accountOptionValue}
							getOptionLabel={(data) => accountOptionLabel(data, t)}
							formatOptionLabel={(data) => accountFormatOptionLabel(data, t)}
							styles={reactSelectStyles<IAccountBuyer, false>(isThemeDark)}
							onChange={(_: SingleValue<IAccountBuyer>) => setAccountId(_?.id ?? 0)}
							isDisabled={!$access.$(ERoleAccess.RevenueSpendAnyAccount)}
							isSearchable={true}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={`input-account-login-kt`}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_kt_group_label)}`}</Form.Label>
						<Form.Control
							disabled={true}
							id={`input-account-login-kt`}
							placeholder=""
							type="text"
							title={currentAccountLoginKt}
							value={currentAccountLoginKt}
							onChange={(e) => {
								//const value = e.target.value
							}}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="input-at">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_date_label)}`}</Form.Label>
						<Form.Control
							id="input-at"
							placeholder={`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_date_placeholder)}`}
							type="date"
							value={dt.utc(at).format('YYYY-MM-DD')}
							disabled={false}
							onChange={
								(e) => {
									const v = dt.utc(e.target.value)
									v.isValid() && setAt(v.startOf('day').toDate())
								}
							}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor="input-amount">{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_amount_label)}`}</Form.Label>
						<Form.Control
							id="input-amount"
							placeholder="0"
							type="number"
							step={0.0001}
							value={amountInput}
							disabled={false}
							onChange={(e) => setAmountInput(e.target.value)}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-notes'}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_notes_label)}`}</Form.Label>
						<Form.Control
							id={'input-notes'}
							placeholder={`${t(ALL_LANG_KEY.revenue_spend_crud_modal.field_notes_placeholder)}`}
							type="text"
							multiple={true}
							disabled={false}
							value={notes ?? ''}
							onChange={(e) => setNotes(e.target.value)}
						/>
						<br/>

					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onUpdate}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.btn_update)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.revenue_spend_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
